<template>
  <div class="integration-shell">
    <header class="integration-heading">
      <img src="/m_s_logo.svg" alt="M+S" width="92">
      <div>
        <h1>Produktkonfigurator</h1>
        <p>External Shop Integration · Demo Mode</p>
      </div>
      <span v-if="context" class="integration-identity">{{ context.identityView.customerKey || context.identityView.externalSubject }}</span>
    </header>
    <section v-if="errorMessage" class="integration-state" role="alert">
      <h2>Fehler beim Demo-Start oder während der Konfiguration</h2>
      <p>{{ errorMessage }}</p>
      <button v-if="demoAllowed" type="button" @click="restartDemo">Demo neu starten</button>
    </section>
    <section v-else-if="!ready" class="integration-state" role="status">{{ statusMessage }}</section>
    <MainComponent v-else ref="configurator" />
  </div>
</template>

<script>
import MainComponent from '@/components/MainComponent.vue';
import { CONFIGURATOR_CONTEXT, createExternalConfiguratorContext } from '@/configurator/ConfiguratorContext';
import { createIntegrationClient } from './integrationClient';
import { isLocalIntegrationDemo } from './demoEnvironment';
import '@fortawesome/fontawesome-free/css/all.css';

export default {
  name: 'IntegrationApp',
  components: { MainComponent },
  data() {
    return {
      context: null, client: null, ready: false,
      statusMessage: 'Verbindung wird hergestellt …',
      errorMessage: '', demoAllowed: isLocalIntegrationDemo(),
    };
  },
  provide() {
    // Resolve the current context lazily; MainComponent mounts only after start.
    return { [CONFIGURATOR_CONTEXT]: () => this.context };
  },
  mounted() { this.startDemo(); },
  beforeUnmount() { this.client?.clearToken(); },
  methods: {
    async startDemo() {
      if (!this.demoAllowed) {
        this.errorMessage = 'Die automatische Testanmeldung ist nur auf einem lokalen Entwicklungsserver verfügbar.';
        return;
      }
      this.client = createIntegrationClient({
        onExpired: () => {
          this.ready = false;
          this.errorMessage = 'Die Integrationssession ist abgelaufen oder wurde beendet. Bitte die Demo neu starten.';
        },
      });
      try {
        const { data } = await this.client.post('/sessions', {
          partnerKey: 'test-partner', externalProof: 'test-proof-a',
        });
        this.client.setToken(data.token);
        this.context = createExternalConfiguratorContext(data, this.client);
        this.statusMessage = 'Konfigurator wird geladen …';
        await this.context.api.loadProject();
        await this.context.api.loadCatalog();
        this.ready = true;
        await this.$nextTick();
        this.$refs.configurator.setSelectedProject(this.context.project);
        await this.$refs.configurator.refreshProject();
      } catch (error) {
        this.client.clearToken();
        this.ready = false;
        const status = error?.response?.status;
        this.errorMessage = status === 426
          ? 'Die Frontendversion ist zu alt. Bitte neu laden oder einen aktuellen Entwicklungsserver starten.'
          : 'Der lokale Demo-Start wurde abgelehnt oder das Backend ist nicht erreichbar. Bitte Backend (local/testing), API-Adresse und IntegrationDemoSeeder prüfen.';
      }
    },
    async restartDemo() {
      if (this.context && this.client) {
        try { await this.client.delete('/session'); } catch { /* Expired sessions need no second logout. */ }
      }
      this.client?.clearToken();
      this.ready = false;
      this.context = null;
      this.errorMessage = '';
      this.statusMessage = 'Verbindung wird hergestellt …';
      await this.$nextTick();
      await this.startDemo();
    },
  },
};
</script>

<style scoped>
.integration-shell { max-width: 1500px; margin: auto; padding: 20px; color: #28333b; font-family: Arial, sans-serif; }
.integration-heading { display: flex; align-items: center; gap: 20px; padding: 12px 0 24px; border-bottom: 1px solid #dbe3e7; margin-bottom: 24px; }
h1 { margin: 0; font-size: 24px; }
.integration-heading p { margin: 7px 0 0; color: #61717d; }
.integration-identity { margin-left: auto; padding: 8px 12px; background: #eef4f6; border-radius: 6px; }
.integration-state { padding: 32px; background: #f3f6f8; border-radius: 8px; line-height: 1.6; }
.integration-state button { padding: 12px 18px; cursor: pointer; }
.integration-shell :deep(.component-container) { width: 100%; max-width: none; margin: 0; }
.integration-shell :deep(.external-no-prices > .left-right-container) { grid-template-columns: minmax(0, 1fr) minmax(0, 28%); grid-template-rows: auto minmax(0, 1fr); grid-template-areas: "filters summary" "configuration summary"; column-gap: 24px; align-items: start; }
.integration-shell :deep(.external-no-prices > .left-right-container > .right) { width: 100%; min-width: 0; }
.integration-shell :deep(.external-no-prices > .left-right-container > .left) { width: 100%; max-width: 360px; padding-top: 30px; scale: 1; }
.integration-shell :deep(.external-no-prices .configured-article-container) { border: 0; margin-bottom: 12px; }
.integration-shell :deep(.cartComponent) { position: static; height: auto; min-height: 0; background: white; }
.integration-shell :deep(.external-showing-cart > .configurator-header),
.integration-shell :deep(.external-showing-cart > .left-right-container),
.integration-shell :deep(.external-showing-cart > .project-readonly-banner) { display: none; }
.integration-shell :deep(.logo) { width: 80px; height: auto; }
.integration-shell :deep(.logo-container) { margin: 0 20px 0 0; }
.integration-shell :deep(.external-cart-item .product-details) { flex-direction: column; gap: 16px; }
.integration-shell :deep(.external-cart-item .one-line) { grid-template-columns: 34px 60px 70px minmax(200px, 1fr) 100px 90px 110px; gap: 16px; }
.integration-shell :deep(.external-cart-item .one-line > .header-value) { grid-column: auto; grid-row: auto; border: 0; padding-top: 0; overflow-wrap: normal; }
.integration-shell :deep(.external-cart-item .one-line > .header-value:nth-child(n+5)) { white-space: nowrap; }
.integration-shell :deep(.external-cart-item .image-container) { width: 70px; height: 70px; }
.integration-shell :deep(.external-cart-item .right) { width: 100%; flex: auto; }
.integration-shell :deep(.external-cart-item .right .content) { flex-direction: row; justify-content: flex-end; gap: 20px; }
.integration-shell :deep(.external-prices-hidden .one-line) { grid-template-columns: 34px 60px 70px minmax(200px, 1fr) 90px; }
.integration-shell :deep(.external-cart-summary) { padding: 24px; background: #eef4f6; border-radius: 8px; }
.integration-shell :deep(.external-cart-summary button) { padding: 12px 20px; margin: 12px 12px 0 0; cursor: pointer; }
.integration-shell :deep(.external-cart-message) { padding: 12px; background: #fff4df; line-height: 1.5; }
@media (max-width: 700px) { .integration-shell { padding: 10px; } .integration-heading { flex-wrap: wrap; } .integration-identity { margin-left: 0; } }
@media (max-width: 700px) {
  .integration-shell :deep(.external-no-prices > .left-right-container) { grid-template-columns: minmax(0, 1fr); grid-template-rows: auto; grid-template-areas: "filters" "configuration" "summary"; gap: 12px; }
  .integration-shell :deep(.external-no-prices > .left-right-container > .left),
  .integration-shell :deep(.external-no-prices > .left-right-container > .right) { flex: auto; width: 100%; max-width: none; }
  .integration-shell :deep(.external-no-prices > .left-right-container > .left) { padding-top: 10px; }
}
@media (max-width: 1050px) {
  .integration-shell :deep(.external-cart-item .one-line) { grid-template-columns: repeat(3, minmax(80px, 1fr)); }
  .integration-shell :deep(.external-cart-item .one-line > .header-value:nth-child(4)) { grid-column: 1 / -1; }
}
</style>
